/**
 * LoadingBar component
 * Elegant, refined loading bar for track/audio loading
 * Warm dark palette with amber accents
 */

import React from 'react';

interface LoadingBarProps {
  /** Loading progress 0-100 */
  progress: number;
  /** Loading status text */
  status?: string;
  /** Track title being loaded */
  trackTitle?: string;
}

export const LoadingBar: React.FC<LoadingBarProps> = ({
  progress,
  status = 'Loading track...',
  trackTitle,
}) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  return (
    <div className="absolute inset-0 z-40 flex flex-col items-center justify-center p-8 bg-studio-950">
      <div className="relative w-full max-w-md">
        {/* Main card */}
        <div className="relative card-editorial rounded-2xl p-8">
          {/* Title */}
          <div className="text-center mb-8">
            <h3 className="font-display text-xl text-stone-100 mb-2">
              {status}
            </h3>
            {trackTitle && (
              <p className="text-amber-500 text-sm font-medium truncate">
                {trackTitle}
              </p>
            )}
          </div>

          {/* Progress bar container */}
          <div className="relative mb-4">
            {/* Track background */}
            <div className="h-2 bg-studio-800 rounded-full overflow-hidden">
              {/* Progress fill with warm gradient */}
              <div
                className="h-full rounded-full transition-all duration-300 ease-out relative"
                style={{
                  width: `${clampedProgress}%`,
                  background: 'linear-gradient(90deg, #f5a623 0%, #e8b86d 100%)',
                }}
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 overflow-hidden rounded-full">
                  <div 
                    className="absolute inset-0 animate-shimmer"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transform: 'translateX(-100%)',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Glow effect on progress */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full blur-md transition-all duration-300"
              style={{
                left: `calc(${clampedProgress}% - 8px)`,
                background: '#f5a623',
                opacity: clampedProgress > 0 ? 0.5 : 0,
              }}
            />
          </div>

          {/* Percentage and details */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-stone-500">
              {clampedProgress < 100 ? 'Downloading audio...' : 'Ready to practice'}
            </span>
            <span 
              className="font-mono font-bold text-lg"
              style={{
                color: clampedProgress >= 100 ? '#81b29a' : '#f5a623',
              }}
            >
              {Math.round(clampedProgress)}%
            </span>
          </div>

          {/* Animated dots for loading state */}
          {clampedProgress < 100 && (
            <div className="flex justify-center gap-1.5 mt-6">
              {[0, 1, 2].map((i) => (
                <div
                  key={`dot-${i}`}
                  className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"
                  style={{
                    animationDelay: `${i * 0.15}s`,
                    animationDuration: '1s',
                  }}
                />
              ))}
            </div>
          )}

          {/* Ready indicator */}
          {clampedProgress >= 100 && (
            <div className="flex items-center justify-center gap-2 mt-6 text-sage-400">
              <svg 
                className="w-5 h-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5 13l4 4L19 7" 
                />
              </svg>
              <span className="font-medium">Audio loaded successfully</span>
            </div>
          )}
        </div>
      </div>

      {/* Tips */}
      <p className="text-stone-600 text-xs mt-6 text-center max-w-sm">
        First load may take longer. Audio is cached for faster playback next time.
      </p>

      {/* Add shimmer animation styles */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
};

export default LoadingBar;
